import { IconButton, Select } from '@hue/design-system';
import { Minus, Plus } from 'lucide-react';
import type { SaleCommissionOptions } from '../../lib/api.js';
import {
  NO_COMMISSION,
  changeShareQuantity,
  choosePerson,
  commissionProblem,
  splitCommission,
  type LineCommission,
} from './sale-commission.js';

const SPLIT = 'split';

function toValue(resourceId: string | null) {
  return resourceId ?? NO_COMMISSION;
}
function toResource(value: string) {
  return value === NO_COMMISSION ? null : value;
}

/// Quem recebe a comissão, dentro do item (ADR 0082, decisão 5). Não há campo geral nem janela: com
/// quantidade maior que 1, a própria lista oferece dividir, e as partes abrem aqui mesmo.
export function SaleCommissionField({
  label,
  commission,
  quantity,
  receivers,
  assistant,
  onChange,
}: {
  label: string;
  commission: LineCommission;
  quantity: number;
  receivers: SaleCommissionOptions['receivers'];
  /// O serviço tem parte de assistente na regra (decisão 24).
  assistant: boolean;
  onChange: (next: LineCommission) => void;
}) {
  const people = [
    { value: NO_COMMISSION, label: 'Sem comissão' },
    ...receivers.map((one) => ({ value: one.id, label: one.displayName })),
  ];
  const split = commission.shares.length > 1;
  const canSplit = quantity > 1 && commission.shares.at(-1)!.quantity > 1;
  const principal = commission.shares[0]!.resourceId;
  const problem = commissionProblem(commission);
  return (
    <div className="grid gap-2 rounded-lg border border-surface-border p-2">
      {commission.shares.map((share, index) => {
        const name = split
          ? `Parte ${String(index + 1)} da comissão de ${label}`
          : `Comissão de ${label}`;
        return (
          <div key={index} className="grid gap-1">
            <Select
              label={split ? `Comissão, parte ${String(index + 1)}` : 'Comissão'}
              value={toValue(share.resourceId)}
              options={[
                ...people,
                ...(canSplit && index === commission.shares.length - 1
                  ? [{ value: SPLIT, label: 'Dividir entre profissionais' }]
                  : []),
              ]}
              onValueChange={(value) =>
                onChange(
                  value === SPLIT
                    ? splitCommission(commission)
                    : choosePerson(commission, index, toResource(value)),
                )
              }
            />
            {split ? (
              <div className="flex items-center justify-end gap-1">
                <IconButton
                  label={`Diminuir ${name}`}
                  icon={<Minus className="size-4" />}
                  onClick={() => onChange(changeShareQuantity(commission, index, -1))}
                />
                <span className="w-8 text-center text-sm tabular-nums">
                  {String(share.quantity)}
                </span>
                <IconButton
                  label={`Aumentar ${name}`}
                  icon={<Plus className="size-4" />}
                  disabled={commission.shares.every(
                    (other, position) => position === index || other.quantity === 1,
                  )}
                  onClick={() => {
                    const giver = commission.shares.findLastIndex(
                      (other, position) => position !== index && other.quantity > 1,
                    );
                    onChange(
                      changeShareQuantity(changeShareQuantity(commission, giver, -1), index, 1),
                    );
                  }}
                />
              </div>
            ) : null}
          </div>
        );
      })}
      {problem ? (
        <p role="alert" className="text-xs text-danger">
          {problem}
        </p>
      ) : null}
      {assistant && !split && principal ? (
        <Select
          label="Assistente"
          value={commission.assistantResourceId ?? ''}
          options={[
            { value: '', label: 'Sem assistente' },
            ...receivers
              .filter((one) => one.id !== principal)
              .map((one) => ({ value: one.id, label: one.displayName })),
          ]}
          onValueChange={(value) => onChange({ ...commission, assistantResourceId: value || null })}
        />
      ) : null}
    </div>
  );
}
